import React from 'react';
import TranslationsContext from 'providers/translationsProvider';
import instance from 'services/api';
import { message } from 'antd';
import { NoConnectionMessage } from 'components/elements';
import { captureException } from '@sentry/browser';

const NO_ERR = null;
const MESSAGES_DELAY = 1.5;

const isBridgeRequest = config =>
  config.baseURL.includes('bridgeforbillions') ||
  config.baseURL.includes('bfb') ||
  config.baseURL.includes('localhost');

export default function ApiMessageHandler() {
  const { t } = React.useContext(TranslationsContext);
  const [err, setError] = React.useState(null);

  React.useEffect(() => {
    message.config({
      top: 12,
      maxCount: 1,
      duration: 2,
    });
  }, []);

  const DATA_NOT_SAVED = t('New data is not saved');
  const SERVER_ERR = t(
    'Please, refresh the page by clicking here and try again. If this problem persists, reach out to us via the chat.'
  );

  const errorMessageHandler = error => {
    let errorMessage = null;
    const isNetworkError = error.message === 'Network Error';
    const isTimeout = error.code === 'ECONNABORTED';

    if (error && error.response && window.location.pathname.indexOf('login') === -1) {
      const responseError = error.response.data && error.response.data.detail;
      if (responseError) {
        if (
          error.response.config.url.includes('/public_programs/') ||
          error.response.config.url.includes('/demo-projects/') ||
          error.response.config.url.includes('/customizations/')
        ) {
          errorMessage = null;
        } else {
          if (error.response.config.method === 'post') {
            errorMessage = t('An error occurred while submitting your data. Please try again.');
          } else if (error.response.config.method === 'patch') {
            errorMessage = DATA_NOT_SAVED;
          } else {
            errorMessage = `Error: ${responseError}`;
          }
        }
      }
      if (error.response.status > 499) {
        setError(SERVER_ERR);
      }
    }

    if (isNetworkError || isTimeout) {
      const messageText = isNetworkError
        ? t('Network problem! Please check your internet connection.')
        : t('Timeout! Please try again.');

      setError(SERVER_ERR);
      message.error(messageText, MESSAGES_DELAY * 2);

      captureException(error, {
        tags: {
          networkError: isNetworkError,
          timeout: isTimeout,
        },
        extra: {
          axiosConfig: error.config,
          axiosResponse: error.response ? error.response.data : null,
        },
      });
    }

    if (errorMessage) message.error(errorMessage, MESSAGES_DELAY * 2);
    throw error;
  };

  const responseMessageHandler = response => {
    if (
      response &&
      response.status &&
      response.status < 300 &&
      isBridgeRequest(response.config) &&
      (response.config.method === 'patch' || response.config.method === 'post') &&
      window.location.pathname.indexOf('login') === -1
    ) {
      setError(NO_ERR);
      setTimeout(() => {
        message.success(t('Data saved'));
      }, MESSAGES_DELAY / 2);
    }
    return response;
  };

  const requestMessageHandler = config => {
    if (isBridgeRequest(config) && (config.method === 'patch' || config.method === 'post')) {
      if (window.location.pathname.indexOf('login') === -1) {
        if (navigator.onLine) {
          message.loading(t('Saving...'), MESSAGES_DELAY);
        } else {
          message.error(t(DATA_NOT_SAVED), MESSAGES_DELAY);
        }
      }
    }
    return config;
  };

  // Add a response interceptor for success and error message handling
  instance.interceptors.request.use(requestMessageHandler);

  // Add a response interceptor for success and error message handling
  instance.interceptors.response.use(responseMessageHandler, errorMessageHandler);

  if (err) return <NoConnectionMessage message={t(err)} t={t} />;
  return null;
}
